import { isNull, omitBy, set } from 'lodash-es';

import {
  startListening,
  trackEvent,
  isAnyOf,
  appOpened,
} from '@betty/toolkit/state';
import {
  fetchRegistrationConfig,
  removeStep,
  restoreStep,
  skipStep,
} from '../registrationConfig/registrationConfig.slice';
import {
  resetRegistrationData,
  selectRegistrationFormConfigId,
  selectRegistrationFormData,
  setFormDataConfigId,
  setReturnToSummary,
  setValidationErrors,
  submitRegistrationForm,
  updateMarketingMeta,
  updateReferrerId,
  updateRegistrationData,
} from './registrationForm.slice';
import { fetchRegistrationOffers } from '../registrationOffers/registrationOffers.slice';
import { RefinedRegistrationDataSchema } from './registrationForm.schema';
import { industriesWithoutOccupation } from './registrationForm.constants';
import {
  extractMarketingMeta,
  mapRegistrationCompletedEventData,
} from './registrationForm.utils';

startListening({
  actionCreator: appOpened,
  effect: (_, { dispatch }) => {
    const searchParams = new URLSearchParams(window?.location?.search);
    const location = window?.location?.pathname;
    const rawMarketingMeta = extractMarketingMeta(searchParams);
    const referralCode = location
      ?.match(/^\/join\/(.+)/g)?.[0]
      ?.split('/join/')[1];

    if (referralCode) {
      rawMarketingMeta.referral_code = referralCode;
    }

    const marketingMeta = omitBy(rawMarketingMeta, isNull);

    dispatch(
      updateRegistrationData({
        marketingMeta: marketingMeta,
      }),
    );
    dispatch(updateMarketingMeta({ ...marketingMeta }));
  },
});

startListening({
  actionCreator: fetchRegistrationConfig.fulfilled,
  effect: (action, { dispatch, getState }) => {
    const configId = action.payload.id;
    const state = getState();
    const formDataConfigId = selectRegistrationFormConfigId(state);

    if (!formDataConfigId) {
      dispatch(setFormDataConfigId(configId));
    } else if (configId !== formDataConfigId) {
      dispatch(resetRegistrationData());
      dispatch(setFormDataConfigId(configId));
    }
  },
});

startListening({
  actionCreator: fetchRegistrationOffers.fulfilled,
  effect: (action, { dispatch, getState }) => {
    const state = getState();
    const { promoId } = selectRegistrationFormData(state);

    if (
      !promoId ||
      !action.payload.items.find(({ promotionId }) => promotionId === promoId)
    ) {
      if (action.payload.items.length === 1) {
        dispatch(
          updateRegistrationData({
            promoId: action.payload.items[0].promotionId,
          }),
        );
      } else if (action.payload.items.length) {
        dispatch(updateRegistrationData({ promoId: -1 }));
      }
    }

    if (action.payload.items[0]?.subTypeMeta?.referrerId) {
      dispatch(
        updateReferrerId(action.payload.items[0]?.subTypeMeta?.referrerId),
      );
    }

    if (action.payload.items.length < 2) {
      dispatch(removeStep('offer'));
    }
  },
});

startListening({
  matcher: isAnyOf(updateRegistrationData, resetRegistrationData.fulfilled),
  effect: (_, { dispatch, getState }) => {
    const state = getState();
    const data = selectRegistrationFormData(state);

    const { error } = RefinedRegistrationDataSchema.safeParse(data);

    const issues = error?.issues || [];

    const mappedErrors = issues.reduce(
      (acc, val) => set(acc, val.path.join('.'), val.message),
      {},
    );

    dispatch(setValidationErrors(mappedErrors));
  },
});

startListening({
  actionCreator: updateRegistrationData,
  effect: (action, { dispatch, getState }) => {
    if (!action.payload.industry) {
      return;
    }

    const industry =
      action.payload.industry.charAt(0).toUpperCase() +
      action.payload.industry.slice(1).toLowerCase();

    if (industriesWithoutOccupation.includes(industry)) {
      dispatch(updateRegistrationData({ occupation: industry }));
      dispatch(skipStep('occupation'));
    } else {
      const state = getState();
      const formData = selectRegistrationFormData(state);

      if (industriesWithoutOccupation.includes(formData.occupation)) {
        dispatch(updateRegistrationData({ occupation: '' }));
      }
      dispatch(setReturnToSummary(false));
      dispatch(restoreStep('occupation'));
    }
  },
});

startListening({
  actionCreator: submitRegistrationForm.fulfilled,
  effect: (action, { dispatch }) => {
    const params = mapRegistrationCompletedEventData(action.payload);

    dispatch(trackEvent({ name: 'registration_process_completed', params }));
  },
});
